import React, { useState, useEffect, useRef } from "react";
import { eosManuals } from "./eosManuals";

const ITEM_WIDTH = 360; 

export default function EosTraining() {
  const [dbDevices, setDbDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [boardItems, setBoardItems] = useState([]); 
  const [connections, setConnections] = useState([]); 
  const [hoveredInst, setHoveredInst] = useState(null); 
  
  const [motorStatus, setMotorStatus] = useState({}); 
  const [isTesting, setIsTesting] = useState(false);
  
  // VIRTUAL MEMORY & HARDWARE STATE
  const [listeningPlugin, setListeningPlugin] = useState(null); 
  const [qaPairingState, setQaPairingState] = useState({}); 
  const [controllerModes, setControllerModes] = useState({}); 
  const [pairedDevices, setPairedDevices] = useState([]); 
  const [flashPlugin, setFlashPlugin] = useState(null); 
  const [friedControllers, setFriedControllers] = useState({}); 
  
  // INTERNAL STOPWATCHES
  const qaTimeouts = useRef({});
  const remoteTimeouts = useRef({});

  // HDSD MANUAL STATE
  const [activeManual, setActiveManual] = useState(null);
  const [activeChannels, setActiveChannels] = useState({}); 
  const [imgHeights, setImgHeights] = useState({}); 
  const boardRef = useRef(null);

  useEffect(() => {
    fetch("/api/data?scope=eos_training&action=list_devices")
      .then(res => res.json())
      .then(data => {
        if (data.ok && data.items) {
          const parsed = data.items.map(d => ({ ...d, terminals: typeof d.terminals === "string" ? JSON.parse(d.terminals) : d.terminals }));
          setDbDevices(parsed);
        }
        setIsLoading(false);
      }).catch(err => { console.error(err); setIsLoading(false); });
  }, []);

  const spawnOnBoard = (device) => {
    const newInstance = { instanceId: `inst-${Date.now()}`, ...device, x: 100 + (boardItems.length * 30), y: 100 + (boardItems.length * 30) };
    setBoardItems([...boardItems, newInstance]);
  };

  const removeDevice = (instanceId) => {
    setBoardItems(prev => prev.filter(item => item.instanceId !== instanceId));
    setConnections(prev => prev.filter(c => c.fromInst !== instanceId && c.toInst !== instanceId));
    setPairedDevices(prev => prev.filter(p => p.pluginId !== instanceId && p.remoteId !== instanceId));
    
    setImgHeights(prev => { const next = {...prev}; delete next[instanceId]; return next; });
    setFriedControllers(prev => { const next = {...prev}; delete next[instanceId]; return next; });
    setActiveChannels(prev => { const next = {...prev}; delete next[instanceId]; return next; });
    setQaPairingState(prev => { const next = {...prev}; delete next[instanceId]; return next; });
    setControllerModes(prev => { const next = {...prev}; delete next[instanceId]; return next; });
  };

  const handleDragDevice = (e, instanceId) => {
    e.dataTransfer.setData("moveInstance", instanceId);
    const rect = e.target.getBoundingClientRect();
    e.dataTransfer.setData("offsetX", e.clientX - rect.left);
    e.dataTransfer.setData("offsetY", e.clientY - rect.top);
  };

  const handleBoardDrop = (e) => {
    e.preventDefault();
    const instanceId = e.dataTransfer.getData("moveInstance");
    if (!instanceId) return;
    const boardRect = boardRef.current.getBoundingClientRect();
    const newX = e.clientX - boardRect.left - Number(e.dataTransfer.getData("offsetX"));
    const newY = e.clientY - boardRect.top - Number(e.dataTransfer.getData("offsetY"));
    setBoardItems(prev => prev.map(item => item.instanceId === instanceId ? { ...item, x: newX, y: newY } : item));
  };

  const handleWireDragStart = (e, instanceId, port) => {
    e.stopPropagation(); e.dataTransfer.setData("wireData", JSON.stringify({ instId: instanceId, port: port }));
  };

  const handleWireDrop = (e, targetInstId, targetPort) => {
    e.preventDefault(); e.stopPropagation();
    const wireDataStr = e.dataTransfer.getData("wireData");
    if (!wireDataStr) return;
    const sourceData = JSON.parse(wireDataStr);
    const sourceInst = boardItems.find(i => i.instanceId === sourceData.instId);
    const sourcePort = sourceInst.terminals.find(t => t.id === sourceData.port.id);

    const isPortFull = connections.some(c => (c.toInst === targetInstId && c.toPort.id === targetPort.id) || (c.fromInst === targetInstId && c.fromPort.id === targetPort.id));
    if (isPortFull) { alert("🛑 Khoan đã bro! Lỗ cắm này đầy rồi. Rút dây cũ ra trước đã."); return; }

    const acceptedRules = targetPort.rule.toLowerCase().split(",").map(r => r.trim());
    const incomingRule = (sourcePort.rule || "").toLowerCase();
    
    // ---> NEW: MA400 BUS FRY LOGIC <---
    const isIncomingAC = ["ac-brown", "ac-blue", "live", "neutral", "brown", "red", "blue"].some(s => incomingRule.includes(s));
    const isTargetData = ["bus-a", "bus-b", "bus", "data", "rs232", "5v"].some(s => acceptedRules.some(r => r.includes(s)));

    if (isIncomingAC && isTargetData) {
      setFriedControllers(prev => ({...prev, [targetInstId]: true}));
      alert("💥 BÙM! Anh em cắm nhầm điện 220V vào cổng tín hiệu mạng (5V) rồi! Bo mạch MA400 cháy đen thui!");
      const newConnection = { id: `conn-${Date.now()}`, fromInst: sourceInst.instanceId, fromPort: sourcePort, toInst: targetInstId, toPort: targetPort, color: sourcePort.color || "#333" };
      setConnections([...connections, newConnection]);
      return;
    }

    let isMatch = acceptedRules.includes(incomingRule);

    if (!isMatch) {
      const isIncomingMains = incomingRule.includes("ac-brown") || incomingRule.includes("ac-blue");
      const isTargetMains = acceptedRules.some(r => r.includes("ac-brown") || r.includes("ac-blue"));
      if (isIncomingMains && isTargetMains) isMatch = true; 
    }

    if (!isMatch) { 
      alert(`💥 CẮM LỘN DÂY RỒI BRO! Lỗ này không nhận dây ${sourcePort.label}.`); 
      return; 
    }

    const newConnection = { id: `conn-${Date.now()}`, fromInst: sourceInst.instanceId, fromPort: sourcePort, toInst: targetInstId, toPort: targetPort, color: sourcePort.color || "#333" };
    setConnections([...connections, newConnection]);
  };

  const handleRemoveWire = (portId) => { setConnections(prev => prev.filter(c => c.toPort.id !== portId)); };

  const checkPowerStatus = (targetDevice) => {
    let controllerToCheck = targetDevice;
    if (targetDevice.device_type === "plugin") {
      const pluginConn = connections.find(c => c.fromInst === targetDevice.instanceId);
      if (!pluginConn) { alert(`🔌 Mất điện! Cục ${targetDevice.name} phải cắm vô bộ điều khiển mới có điện xài.`); return false; }
      controllerToCheck = boardItems.find(i => i.instanceId === pluginConn.toInst);
      if (!controllerToCheck) return false;
    }
    if (friedControllers[controllerToCheck.instanceId]) { alert("☠️ Báo cháy! Thiết bị này tiêu cmnr. Xóa đi lấy cục mới ra xài bro."); return false; }

    let isFried = false;
    let hasLive = false;
    let hasNeutral = false;
    const deviceConnections = connections.filter(c => c.toInst === controllerToCheck.instanceId || c.fromInst === controllerToCheck.instanceId);
    
    deviceConnections.forEach(conn => {
      const isMyOutput = conn.fromInst === controllerToCheck.instanceId;
      const myPort = isMyOutput ? conn.fromPort : conn.toPort;
      const theirInstId = isMyOutput ? conn.toInst : conn.fromInst;
      const theirPort = isMyOutput ? conn.toPort : conn.fromPort;
      const theirDevice = boardItems.find(i => i.instanceId === theirInstId);
      if (!theirDevice) return;

      if (theirDevice.device_type === "motor" || theirDevice.name.toLowerCase().includes("hd60") || theirDevice.name.toLowerCase().includes("curtain")) return; 

      const isConnectedToPower = theirDevice.device_type === "power" || theirDevice.name.toLowerCase().includes("nguồn") || theirDevice.name.toLowerCase().includes("socket") || theirDevice.name.toLowerCase().includes("mains");
      const myPortRule = (myPort.rule || "").toLowerCase();
      const myLabel = (myPort.label || "").toLowerCase();
      const theirPortRule = (theirPort.rule || "").toLowerCase();
      const theirLabel = (theirPort.label || "").toLowerCase();

      const isLive = [myPortRule, myLabel, theirPortRule, theirLabel].some(s => s.includes("brown") || s.includes("red") || s.includes("mains-l") || s.includes("live") || s.includes("nóng"));
      const isNeutral = [myPortRule, myLabel, theirPortRule, theirLabel].some(s => s.includes("blue") || s.includes("white") || s.includes("mains-n") || s.includes("neutral") || s.includes("lạnh"));
      const isEarth = [myPortRule, myLabel, theirPortRule, theirLabel].some(s => s.includes("earth") || s.includes("green") || s.includes("mains-e"));

      if (isConnectedToPower) {
        if (isLive) hasLive = true;
        if (isNeutral) hasNeutral = true;
      } else {
        const activeSignal = isMyOutput ? theirPortRule : (conn.fromPort.rule || "").toLowerCase();
        if (isLive) {
            if (activeSignal.includes("brown") || activeSignal.includes("red")) hasLive = true;
            else if (activeSignal.includes("blue") || activeSignal.includes("white") || activeSignal.includes("earth") || activeSignal.includes("green")) isFried = true;
        }
        if (isNeutral) {
            if (activeSignal.includes("blue") || activeSignal.includes("white")) hasNeutral = true;
            else if (activeSignal.includes("brown") || activeSignal.includes("red")) isFried = true;
        }
        if (isEarth) { if (activeSignal.includes("brown") || activeSignal.includes("red")) isFried = true; }
      }
    });

    if (isFried) { setFriedControllers(prev => ({...prev, [controllerToCheck.instanceId]: true})); alert("💥 BÙM! Dây điện chập mạch rồi! Đi tong thiết bị rồi bro."); return false; }
    if (!hasLive || !hasNeutral) { alert(`🔌 Cúp điện! Phải cắm đủ dây Nóng (Nâu/Đỏ) và Lạnh (Xanh/Trắng) mới chạy được thiết bị ${controllerToCheck.name}.`); return false; }
    return true; 
  };

  const handleQA200MouseDown = (e, instId) => {
    e.preventDefault(); e.stopPropagation();
    const receiverInst = boardItems.find(i => i.instanceId === instId);
    if (!checkPowerStatus(receiverInst)) return;

    if (qaTimeouts.current[instId]) {
      qaTimeouts.current[instId].forEach(clearTimeout);
    }
    qaTimeouts.current[instId] = [];

    const t3 = setTimeout(() => {
      setQaPairingState(prev => {
        const st = prev[instId];
        if (st === "M1") return { ...prev, [instId]: "M2" }; 
        return { ...prev, [instId]: "M1" }; 
      });
    }, 3000);

    const t8 = setTimeout(() => {
      setQaPairingState(prev => ({ ...prev, [instId]: "MODE" })); 
    }, 8000);

    qaTimeouts.current[instId].push(t3, t8);
  };

  const handleQA200MouseUp = (e, instId) => {
    e.preventDefault(); e.stopPropagation();
    if (qaTimeouts.current[instId]) {
      qaTimeouts.current[instId].forEach(clearTimeout);
      qaTimeouts.current[instId] = [];
    }
    const currentState = qaPairingState[instId];
    if (currentState) {
      setTimeout(() => setQaPairingState(curr => ({ ...curr, [instId]: null })), 15000);
    }
  };

  const handleInteractiveButton = (e, instId, actionCode, isLongPress = false) => {
    e.stopPropagation(); 
    const code = (actionCode || "").trim().toLowerCase();

    if (code === "tq4-channel") {
      const current = activeChannels[instId] || "1";
      const next = current === "1" ? "2" : current === "2" ? "3" : current === "3" ? "4" : current === "4" ? "ALL" : "1";
      setActiveChannels(prev => ({...prev, [instId]: next}));
      return;
    }

    const isDeviceSmartMotor = (device) => {
      return device && (device.device_type === "motor" || device.name.toLowerCase().includes("hd60") || device.name.toLowerCase().includes("curtain"));
    };

    if (code.includes("set")) {
      const receiverInst = boardItems.find(i => i.instanceId === instId);
      const isQA = receiverInst && receiverInst.name.toLowerCase().includes("qa200");
      if (isQA) return; 

      if (receiverInst && (
          receiverInst.device_type === "controller" || 
          receiverInst.device_type === "plugin" || 
          receiverInst.name.toLowerCase().includes("ma400") || 
          isDeviceSmartMotor(receiverInst)
      )) {
        if (!checkPowerStatus(receiverInst)) return; 
        setListeningPlugin(instId);
        setTimeout(() => setListeningPlugin(null), 10000); 
      }
      return;
    }

    if (code.includes("stop")) {
      setMotorStatus({});
      setIsTesting(false);
      return;
    }

    let direction = "";
    if (code.includes("mode5")) direction = "MODE5"; 
    else if (code.includes("up")) direction = "UP";
    else if (code.includes("down")) direction = "DOWN";

    if (direction) {
      const clickedInst = boardItems.find(i => i.instanceId === instId);
      const isSwitch = clickedInst && clickedInst.device_type === "switch";

      // --- UNIVERSAL SWITCH BYPASS ---
      if (isSwitch) {
        if (direction === "MODE5") return; 
        if (!checkPowerStatus(clickedInst)) return; 

        setIsTesting(true);
        let newMotorStatus = { ...motorStatus };
        let foundAction = false;
        let joggedMotors = []; 

        const switchConns = connections.filter(c => c.toInst === instId || c.fromInst === instId);
        const activeConns = switchConns.filter(c => {
          const switchPort = c.toInst === instId ? c.toPort : c.fromPort;
          const rule = (switchPort.rule || "").toLowerCase();
          const label = (switchPort.label || "").toLowerCase();
          
          let isUpPort = label.includes("up") || label === "l" || label.includes("brown") || label.includes("red");
          let isDownPort = label.includes("down") || label === "r" || label.includes("black") || label.includes("white");
          if (!isUpPort && !isDownPort) {
             isUpPort = rule.includes("brown") || rule.includes("red") || rule.includes("up");
             isDownPort = rule.includes("black") || rule.includes("white") || rule.includes("down");
          }
          if (isUpPort && isDownPort) {
             isUpPort = label.includes("brown") || label.includes("up") || label === "l";
             isDownPort = label.includes("black") || label.includes("down") || label === "r";
          }
          if (direction === "UP") return isUpPort;
          if (direction === "DOWN") return isDownPort;
          return false;
        });

        if (activeConns.length === 0) { alert("⚠️ Cổng bạn vừa bấm chưa được cắm dây ra thiết bị nào!"); return; }

        activeConns.forEach(conn => {
          const otherInstId = conn.toInst === instId ? conn.fromInst : conn.toInst;
          const otherDevice = boardItems.find(i => i.instanceId === otherInstId);
          if (!otherDevice) return;
          
          const isMotor = otherDevice.device_type === "motor" || otherDevice.name.toLowerCase().includes("hd60") || otherDevice.name.toLowerCase().includes("curtain");
          const isController = otherDevice.device_type === "controller" || otherDevice.name.toLowerCase().includes("ma400") || otherDevice.name.toLowerCase().includes("qa200");

          if (isMotor) {
              foundAction = true;
              const motorPort = conn.toInst === instId ? conn.fromPort : conn.toPort;
              const motorPortRule = (motorPort.rule || "").toLowerCase();
              
              let spinDirection = "ERROR"; 
              if (motorPortRule.includes("brown") || motorPortRule.includes("up") || motorPortRule.includes("mlwire-brown")) spinDirection = "UP"; 
              else if (motorPortRule.includes("black") || motorPortRule.includes("down") || motorPortRule.includes("mlwire-black")) spinDirection = "DOWN"; 

              if (spinDirection !== "ERROR") {
                if (motorStatus[otherInstId] === spinDirection) delete newMotorStatus[otherInstId];
                else newMotorStatus[otherInstId] = spinDirection;
              }
          } else if (isController) {
              if (!checkPowerStatus(otherDevice)) return; 

              // ---> NEW: MA400 BUS ECHO TRAP <---
              if (otherDevice.name.toLowerCase().includes("ma400")) {
                  const hasResistor = connections.some(c => {
                      const tgtInstId = c.toInst === otherDevice.instanceId ? c.fromInst : (c.fromInst === otherDevice.instanceId ? c.toInst : null);
                      if (!tgtInstId) return false;
                      const connectedDev = boardItems.find(i => i.instanceId === tgtInstId);
                      return connectedDev && (connectedDev.name.toLowerCase().includes("resistor") || connectedDev.name.toLowerCase().includes("điện trở") || connectedDev.name.toLowerCase().includes("120"));
                  });
      
                  if (!hasResistor) {
                      alert("⚠️ LỖI MẠNG BUS: Hộp MA400 chưa được chốt Điện Trở 120-ohm ở cuối đường dây! Tín hiệu bị dội ngược (Echo), hệ thống từ chối lệnh chạy.");
                      return; 
                  }
              }

              foundAction = true;

              let activeTerminals = [];
              if (direction === "UP") activeTerminals = ["m1-up", "m2-up", "m3-up", "m4-up"];
              if (direction === "DOWN") activeTerminals = ["m1-down", "m2-down", "m3-down", "m4-down"];

              activeTerminals.forEach(terminalPrefix => {
                const controllerPort = otherDevice.terminals.find(t => t.label.toLowerCase().startsWith(terminalPrefix));
                if (!controllerPort) return;
                
                const activeConnection = connections.find(c => c.toInst === otherDevice.instanceId && c.toPort.id === controllerPort.id);
                if (activeConnection) {
                  const motorInstId = activeConnection.fromInst;
                  const connectedWireRule = activeConnection.fromPort.rule || ""; 
                  let spinDirection = "ERROR";

                  if (connectedWireRule.includes("mLwire-brown") || connectedWireRule.includes("mRwire-black")) spinDirection = "UP";
                  if (connectedWireRule.includes("mLwire-black") || connectedWireRule.includes("mRwire-brown")) spinDirection = "DOWN";
                  
                  const currentMode = controllerModes[otherDevice.instanceId] || 1;
                  const isPortVenetian = currentMode === 3 || currentMode === 4 || (currentMode === 5 && terminalPrefix.startsWith("m2"));
                  if (isPortVenetian && !isLongPress) spinDirection = "JOG";

                  if (spinDirection === "JOG") {
                    newMotorStatus[motorInstId] = "JOG";
                    joggedMotors.push(motorInstId);
                  } else {
                    if (motorStatus[motorInstId] === spinDirection) delete newMotorStatus[motorInstId]; 
                    else newMotorStatus[motorInstId] = spinDirection;
                  }
                }
              });
          }
        });

        if (foundAction) {
          setMotorStatus(newMotorStatus);
          if (joggedMotors.length > 0) {
            setTimeout(() => {
              setMotorStatus(prev => {
                let next = { ...prev };
                joggedMotors.forEach(mId => { if (next[mId] === "JOG") delete next[mId]; });
                return next;
              });
            }, 600); 
          }
        }
        return; 
      }

      // --- WIRELESS LOGIC ---
      let channelsToFire = [];
      if (code.includes("tq4-")) {
        const currentCh = activeChannels[instId] || "1";
        channelsToFire = currentCh === "ALL" ? ["1", "2", "3", "4"] : [currentCh];
      } else {
        channelsToFire = [code.includes("2") ? "2" : (code.includes("3") ? "3" : (code.includes("4") ? "4" : "1"))];
      }

      const activeQaListenerId = Object.keys(qaPairingState).find(k => qaPairingState[k]);
      const activeListenerId = activeQaListenerId || listeningPlugin;

      if (activeListenerId) {
        const receiverInst = boardItems.find(i => i.instanceId === activeListenerId);
        let targetController = null;
        
        if (receiverInst && (receiverInst.device_type === "controller" || receiverInst.name.toLowerCase().includes("qa200") || receiverInst.name.toLowerCase().includes("ma400") || isDeviceSmartMotor(receiverInst))) {
          targetController = receiverInst;
        } else if (receiverInst && receiverInst.device_type === "plugin") {
          const pluginConn = connections.find(c => c.fromInst === activeListenerId);
          if (pluginConn) targetController = boardItems.find(i => i.instanceId === pluginConn.toInst);
        }

        if (targetController && !checkPowerStatus(targetController)) return;

        const pState = qaPairingState[activeListenerId];

        if (pState === "MODE") {
             if (direction === "UP" || direction === "DOWN" || direction === "MODE5") { 
                 let selectedMode = 1; 
                 const isCh1 = channelsToFire.includes("1");
                 const isCh2 = channelsToFire.includes("2");

                 if (direction === "MODE5") selectedMode = 5; 
                 else if (direction === "UP" && isCh2) selectedMode = 1;       
                 else if (direction === "UP" && isCh1) selectedMode = 2;  
                 else if (direction === "DOWN" && isCh2) selectedMode = 3;
                 else if (direction === "DOWN" && isCh1) selectedMode = 4;
                 else selectedMode = 5;

                 setControllerModes(prev => ({ ...prev, [targetController.instanceId]: selectedMode }));
                 setFlashPlugin(activeListenerId);
                 setTimeout(() => setFlashPlugin(null), 1000); 
             }
        } else if (direction === "UP") {
            setPairedDevices(prev => {
              let updatedPairs = [...prev];
              channelsToFire.forEach(ch => {
                const motorTarget = pState || "M1"; 
                updatedPairs = updatedPairs.filter(p => !(p.pluginId === activeListenerId && p.remoteId === instId && p.remoteCh === ch));
                updatedPairs.push({ pluginId: activeListenerId, remoteId: instId, remoteCh: ch, targetMotor: motorTarget });
              });
              return updatedPairs;
            });

            if (targetController) {
              let jogStatus = { ...motorStatus };
              if (isDeviceSmartMotor(targetController)) {
                jogStatus[targetController.instanceId] = "JOG";
              } else {
                const targetPrefixes = (pState === "M2") ? ["m2-up"] : ["m1-up"];
                targetPrefixes.forEach(terminalPrefix => {
                  const qaPort = targetController.terminals.find(t => t.label.toLowerCase().startsWith(terminalPrefix));
                  if (!qaPort) return;
                  const activeConnection = connections.find(c => c.toInst === targetController.instanceId && c.toPort.id === qaPort.id);
                  if (activeConnection) jogStatus[activeConnection.fromInst] = "JOG"; 
                });
              }
              setMotorStatus(jogStatus);
              setTimeout(() => setMotorStatus({}), 1000); 
            }
        } else if (direction === "DOWN") {
            setPairedDevices(prev => prev.filter(p => !(p.pluginId === activeListenerId && p.remoteId === instId && channelsToFire.includes(p.remoteCh))));
        }
        
        setQaPairingState({});
        setListeningPlugin(null);
        if (pState !== "MODE") {
            setFlashPlugin(activeListenerId);
            setTimeout(() => setFlashPlugin(null), 1500); 
        }
        return;
      }

      if (direction === "MODE5") return; 

      const targetPlugins = pairedDevices.filter(p => p.remoteId === instId && channelsToFire.includes(p.remoteCh));
      if (targetPlugins.length === 0) {
        alert(`🛑 Kênh chưa kết nối! Bấm 'Set' trên cục điều khiển trước, rồi bấm nút trên remote để ghép đôi nha bro.`);
        return;
      }

      setIsTesting(true);
      let newMotorStatus = { ...motorStatus };
      let foundController = false;
      let joggedMotors = []; 

      targetPlugins.forEach(tPlugin => {
        const receiverInst = boardItems.find(i => i.instanceId === tPlugin.pluginId);
        if (!receiverInst) return;

        let targetController = null;
        if (receiverInst && (receiverInst.device_type === "controller" || receiverInst.name.toLowerCase().includes("qa200") || receiverInst.name.toLowerCase().includes("ma400") || isDeviceSmartMotor(receiverInst))) {
          targetController = receiverInst;
        } else {
          const pluginConn = connections.find(c => c.fromInst === tPlugin.pluginId);
          if (!pluginConn) { alert(`⚠️ Thiết bị thu nhận sóng rồi, nhưng CHƯA cắm dây vô bộ điều khiển!`); return; }
          targetController = boardItems.find(i => i.instanceId === pluginConn.toInst);
        }

        if (!targetController || !checkPowerStatus(targetController)) return;

        // ---> NEW: MA400 BUS ECHO TRAP (WIRELESS) <---
        if (targetController.name.toLowerCase().includes("ma400")) {
            const hasResistor = connections.some(c => {
                const tgtInstId = c.toInst === targetController.instanceId ? c.fromInst : (c.fromInst === targetController.instanceId ? c.toInst : null);
                if (!tgtInstId) return false;
                const connectedDev = boardItems.find(i => i.instanceId === tgtInstId);
                return connectedDev && (connectedDev.name.toLowerCase().includes("resistor") || connectedDev.name.toLowerCase().includes("điện trở") || connectedDev.name.toLowerCase().includes("120"));
            });

            if (!hasResistor) {
                alert("⚠️ LỖI MẠNG BUS: Hộp MA400 chưa được chốt Điện Trở 120-ohm ở cuối đường dây! Tín hiệu bị dội ngược (Echo), hệ thống từ chối lệnh chạy.");
                return; 
            }
        }

        foundController = true;

        if (isDeviceSmartMotor(targetController)) {
          if (motorStatus[targetController.instanceId] === direction) delete newMotorStatus[targetController.instanceId];
          else newMotorStatus[targetController.instanceId] = direction;
        } else {
          const currentMode = controllerModes[targetController.instanceId] || 1;
          const isGroupMode = currentMode === 1 || currentMode === 3; 
          const motorTarget = tPlugin.targetMotor || "M1";

          let activeTerminals = [];
          
          if (isGroupMode) {
             if (direction === "UP") activeTerminals = ["m1-up", "m2-up", "m3-up", "m4-up"];
             if (direction === "DOWN") activeTerminals = ["m1-down", "m2-down", "m3-down", "m4-down"];
          } else {
             const targetPrefix = motorTarget === "M2" ? "m2" : "m1";
             if (direction === "UP") activeTerminals = [`${targetPrefix}-up`];
             if (direction === "DOWN") activeTerminals = [`${targetPrefix}-down`];
          }

          activeTerminals.forEach(terminalPrefix => {
            const qaPort = targetController.terminals.find(t => t.label.toLowerCase().startsWith(terminalPrefix));
            if (!qaPort) return;
            const activeConnection = connections.find(c => c.toInst === targetController.instanceId && c.toPort.id === qaPort.id);
            
            if (activeConnection) {
              const motorInstId = activeConnection.fromInst;
              const connectedWireRule = activeConnection.fromPort.rule || ""; 
              let spinDirection = "ERROR";

              if (connectedWireRule.includes("mLwire-brown") || connectedWireRule.includes("mRwire-black")) spinDirection = "UP";
              if (connectedWireRule.includes("mLwire-black") || connectedWireRule.includes("mRwire-brown")) spinDirection = "DOWN";
              
              const isPortVenetian = currentMode === 3 || currentMode === 4 || (currentMode === 5 && terminalPrefix.startsWith("m2"));
              if (isPortVenetian && !isLongPress) spinDirection = "JOG";

              if (spinDirection === "JOG") {
                newMotorStatus[motorInstId] = "JOG";
                joggedMotors.push(motorInstId);
              } else {
                if (motorStatus[motorInstId] === spinDirection) delete newMotorStatus[motorInstId]; 
                else newMotorStatus[motorInstId] = spinDirection;
              }
            }
          });
        }
      });

      if (foundController) { 
        setMotorStatus(newMotorStatus); 
        if (joggedMotors.length > 0) {
          setTimeout(() => {
            setMotorStatus(prev => {
              let next = { ...prev };
              joggedMotors.forEach(mId => { if (next[mId] === "JOG") delete next[mId]; });
              return next;
            });
          }, 600); 
        }
      }
    }
  };

  const getPortCoords = (instanceId, portId) => {
    const item = boardItems.find(i => i.instanceId === instanceId);
    if (!item) return { x: 0, y: 0 };
    const port = item.terminals.find(t => t.id === portId);
    if (!port) return { x: 0, y: 0 };
    const renderedImgHeight = imgHeights[instanceId] || 200; 
    return { x: item.x + (ITEM_WIDTH * (port.position.x / 100)), y: item.y + 25 + (renderedImgHeight * (port.position.y / 100)) };
  };

  let manualData = null;
  if (activeManual) {
    const manualKey = Object.keys(eosManuals).find(key => activeManual.name.toLowerCase().includes(key));
    manualData = eosManuals[manualKey];
  }

  return (
    <div style={{ padding: "20px", fontFamily: "sans-serif", width: "100%", height: "100vh", boxSizing: "border-box", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <a href="/sysadmin/eos-setup" style={{ position: "absolute", top: "20px", right: "30px", textDecoration: "none", fontSize: "24px", opacity: "0.5", zIndex: 1000, cursor: "pointer" }} title="Admin Setup">⚙️</a>
      <h2 style={{ textAlign: "center", borderBottom: "2px solid #ccc", paddingBottom: "10px", margin: "0 0 15px 0", flexShrink: 0 }}>Sandbox: Wire the EOS Network</h2>

      <div style={{ display: "flex", gap: "20px", flex: 1, minHeight: 0 }}>
        
        {/* LEFT: PARTS BIN */}
        <div style={{ width: "240px", flexShrink: 0, border: "1px solid #ccc", padding: "15px", backgroundColor: "#f9f9f9", height: "100%", overflowY: "auto", boxSizing: "border-box", borderRadius: "8px" }}>
          <h3 style={{ marginTop: 0 }}>Warehouse</h3>
          <p style={{ fontSize: "12px", color: "#666", marginBottom: "15px" }}>Click to spawn</p>
          {isLoading ? <p>Loading...</p> : dbDevices.map(dev => (
            <div key={dev.id} onClick={() => spawnOnBoard(dev)} style={{ border: "1px solid #ddd", padding: "10px", marginBottom: "15px", cursor: "pointer", textAlign: "center", backgroundColor: "#fff", borderRadius: "5px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
              <img src={dev.image_url} alt={dev.name} style={{ maxWidth: "100%", maxHeight: "50px", objectFit: "contain" }} />
              <div style={{ marginTop: "10px", fontWeight: "bold", fontSize: "13px" }}>{dev.name}</div>
              <div style={{ fontSize: "10px", color: "#888", textTransform: "uppercase" }}>{dev.device_type}</div>
            </div>
          ))}
        </div>

        {/* CENTER: THE INFINITE CANVAS */}
        <div style={{ flex: 1, border: "2px solid #999", borderRadius: "8px", backgroundColor: "#fafafa", overflow: "auto", position: "relative" }}>
          <div ref={boardRef} onDragOver={(e) => e.preventDefault()} onDrop={handleBoardDrop} style={{ width: "4000px", height: "4000px", position: "relative", backgroundImage: "radial-gradient(#ccc 1px, transparent 1px)", backgroundSize: "20px 20px" }}>
            
            <svg style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 1 }}>
              {connections.map(conn => {
                const start = getPortCoords(conn.fromInst, conn.fromPort.id);
                const end = getPortCoords(conn.toInst, conn.toPort.id);
                const curve = `M ${start.x} ${start.y} C ${start.x} ${end.y}, ${end.x} ${start.y}, ${end.x} ${end.y}`;
                return <path key={conn.id} d={curve} stroke={conn.color} strokeWidth="5" fill="none" strokeDasharray="6,4" />;
              })}
            </svg>

            {boardItems.map(item => {
              const isTQ4 = item.name.toLowerCase().includes("tq") || item.name.toLowerCase().includes("tq-4");
              const isQA200 = item.name.toLowerCase().includes("qa200");
              const currentCh = activeChannels[item.instanceId] || "1";
              
              const isCurtain = item.name.toLowerCase().includes("hd60") || item.name.toLowerCase().includes("curtain");
              const isVenetian = item.name.toLowerCase().includes("venetian") || item.name.toLowerCase().includes("sáo") || item.name.toLowerCase().includes("vb");

              const currentQaState = qaPairingState[item.instanceId];
              let glowBoxShadow = "none";
              if (currentQaState === "M1") glowBoxShadow = "0 0 35px #28a745"; 
              else if (currentQaState === "M2") glowBoxShadow = "0 0 35px #dc3545"; 
              else if (currentQaState === "MODE") glowBoxShadow = "0 0 35px #fd7e14"; 
              else if (listeningPlugin === item.instanceId) glowBoxShadow = "0 0 30px #ffc107";
              else if (flashPlugin === item.instanceId) glowBoxShadow = "0 0 40px #007bff";

              return (
                <div key={item.instanceId} onMouseEnter={() => setHoveredInst(item.instanceId)} onMouseLeave={() => setHoveredInst(null)} style={{ position: "absolute", left: item.x, top: item.y, width: ITEM_WIDTH, backgroundColor: "#fff", border: "1px solid #333", borderRadius: "6px", boxShadow: "0 4px 12px rgba(0,0,0,0.2)", zIndex: hoveredInst === item.instanceId ? 50 : 2 }}>
                  
                  {isQA200 && (
                    <div style={{ position: "absolute", top: "-15px", left: "50%", transform: "translateX(-50%)", backgroundColor: "#333", color: "#ffc107", padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: "bold", zIndex: 20 }}>
                      Mode {controllerModes[item.instanceId] || 1}
                    </div>
                  )}

                  <div draggable onDragStart={(e) => handleDragDevice(e, item.instanceId)} style={{ backgroundColor: friedControllers[item.instanceId] ? "#660000" : "#333", color: "white", padding: "0 10px", fontSize: "11px", height: "25px", boxSizing: "border-box", cursor: "move", display: "flex", justifyContent: "space-between", alignItems: "center", borderTopLeftRadius: "5px", borderTopRightRadius: "5px" }}>
                    <span style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "80%" }}>{friedControllers[item.instanceId] ? "☠️ FRIED: " : ""}{item.name}</span>
                    <span onClick={() => removeDevice(item.instanceId)} style={{ cursor: "pointer", color: "#ff4d4d", fontSize: "14px" }}>✖</span>
                  </div>

                  <div style={{ position: "relative", width: "100%" }}>
                    <img src={item.image_url} alt={item.name} onLoad={(e) => setImgHeights(prev => ({...prev, [item.instanceId]: e.target.clientHeight}))} 
                      onDoubleClick={() => setActiveManual(item)}
                      style={{ 
                        width: "100%", height: "auto", display: "block", borderBottomLeftRadius: "5px", borderBottomRightRadius: "5px", 
                        opacity: motorStatus[item.instanceId] ? 0.4 : 1, transition: "all 0.3s",
                        boxShadow: glowBoxShadow,
                        filter: friedControllers[item.instanceId] ? "grayscale(100%) sepia(80%) hue-rotate(-50deg) saturate(300%) contrast(1.2)" : "none",
                        cursor: "help"
                      }} 
                    />
                    
                    {isTQ4 && (
                      <div style={{ position: "absolute", bottom: "16%", left: "0", width: "100%", display: "flex", justifyContent: "center", gap: "10%", zIndex: 15, pointerEvents: "none" }}>
                        {[1, 2, 3, 4].map(num => {
                          const isOn = currentCh === String(num) || currentCh === "ALL";
                          return (
                            <div key={num} style={{ 
                              width: "10px", height: "10px", borderRadius: "50%", 
                              backgroundColor: isOn ? "#28a745" : "transparent", 
                              boxShadow: isOn ? "0 0 10px #28a745" : "none",
                              transition: "all 0.2s"
                            }} />
                          );
                        })}
                      </div>
                    )}

                    {friedControllers[item.instanceId] && (
                      <div style={{ position: "absolute", top: "0", left: "0", width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "rgba(0,0,0,0.6)", zIndex: 15, pointerEvents: "none", borderBottomLeftRadius: "5px", borderBottomRightRadius: "5px" }}>
                        <div style={{ fontSize: "100px", textShadow: "0 0 20px red", animation: "blinkJog 0.5s ease-in-out" }}>💥</div>
                      </div>
                    )}

                    {motorStatus[item.instanceId] && (
                      <div style={{ position: "absolute", top: "0", left: "0", width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "rgba(0,0,0,0.1)", zIndex: 10, pointerEvents: "none", borderBottomLeftRadius: "5px", borderBottomRightRadius: "5px" }}>
                        <div style={{ 
                          fontSize: isCurtain ? "50px" : "60px", fontWeight: "bold", 
                          color: motorStatus[item.instanceId] === "UP" ? "#28a745" : (motorStatus[item.instanceId] === "JOG" ? "#ffc107" : "#dc3545"), 
                          textShadow: "0px 4px 10px rgba(0,0,0,0.6)", whiteSpace: "nowrap", 
                          animation: motorStatus[item.instanceId] === "UP" 
                            ? (isCurtain ? "curtainOpen 0.8s infinite alternate" : (isVenetian ? "blinkUp 0.6s infinite alternate" : "blinkUp 0.6s infinite alternate")) 
                            : (motorStatus[item.instanceId] === "JOG" 
                                ? (isCurtain ? "blinkJogHorizontal 0.5s ease-in-out" : (isVenetian ? "venetianTilt 0.3s infinite alternate" : "blinkJog 0.5s ease-in-out")) 
                                : (isCurtain ? "curtainClose 0.8s infinite alternate" : (isVenetian ? "blinkDown 0.6s infinite alternate" : "blinkDown 0.6s infinite alternate")))
                        }}>
                          {motorStatus[item.instanceId] === "UP" 
                            ? (isCurtain ? "◀ ▶" : (isVenetian ? "⏫" : "⬆️")) 
                            : (motorStatus[item.instanceId] === "JOG" 
                                ? (isCurtain ? "↔️" : (isVenetian ? "///" : "↕️")) 
                                : (isCurtain ? "▶ ◀" : (isVenetian ? "⏬" : "⬇️")))}
                        </div>
                      </div>
                    )}

                    {(item.terminals || []).map(port => {
                      if (port.type === "button") {
                        const isQA200Set = isQA200 && port.rule.toLowerCase().includes("set");

                        return (
                          <div key={port.id} title={port.label} 
                            onContextMenu={(e) => e.preventDefault()}
                            draggable={false}
                            onMouseDown={(e) => {
                              e.currentTarget.style.transform = "translate(-50%, -50%) scale(0.9)";
                              if (isQA200Set) {
                                handleQA200MouseDown(e, item.instanceId);
                              } else {
                                const ruleLower = port.rule.toLowerCase();
                                if (ruleLower.includes("up") || ruleLower.includes("down")) {
                                    remoteTimeouts.current[port.id] = setTimeout(() => {
                                        handleInteractiveButton(e, item.instanceId, port.rule, true); 
                                        remoteTimeouts.current[port.id] = 'triggered';
                                    }, 3000); 
                                } else {
                                    handleInteractiveButton(e, item.instanceId, port.rule, false);
                                }
                              }
                            }}
                            onMouseUp={(e) => {
                              e.currentTarget.style.transform = "translate(-50%, -50%) scale(1)";
                              if (isQA200Set) {
                                handleQA200MouseUp(e, item.instanceId);
                              } else {
                                const ruleLower = port.rule.toLowerCase();
                                if (ruleLower.includes("up") || ruleLower.includes("down")) {
                                    if (remoteTimeouts.current[port.id] !== 'triggered') {
                                        clearTimeout(remoteTimeouts.current[port.id]);
                                        handleInteractiveButton(e, item.instanceId, port.rule, false);
                                    }
                                    remoteTimeouts.current[port.id] = null;
                                }
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translate(-50%, -50%) scale(1)";
                              if (isQA200Set) {
                                handleQA200MouseUp(e, item.instanceId);
                              } else {
                                if (remoteTimeouts.current[port.id] && remoteTimeouts.current[port.id] !== 'triggered') {
                                    clearTimeout(remoteTimeouts.current[port.id]);
                                }
                                remoteTimeouts.current[port.id] = null;
                              }
                            }}
                            style={{
                              position: "absolute", left: `${port.position.x}%`, top: `${port.position.y}%`, transform: "translate(-50%, -50%)",
                              width: "30px", height: "30px", backgroundColor: "rgba(0,150,255,0.3)", border: "2px solid rgba(0,150,255,0.8)", borderRadius: "50%",
                              cursor: "pointer", zIndex: 20, transition: "transform 0.1s",
                              userSelect: "none", WebkitUserSelect: "none"
                            }}
                          />
                        );
                      }

                      const isOutput = port.type === "output";
                      const connection = connections.find(c => c.fromPort.id === port.id || c.toPort.id === port.id);
                      const isConnected = !!connection;

                      return (
                        <div key={port.id} title={port.label} draggable={isOutput} onDragStart={(e) => isOutput && handleWireDragStart(e, item.instanceId, port)} onDragOver={(e) => { if (!isOutput) e.preventDefault(); }} onDrop={(e) => { if (!isOutput) handleWireDrop(e, item.instanceId, port); }} onClick={() => !isOutput && isConnected && handleRemoveWire(port.id)} 
                          style={{
                            position: "absolute", left: `${port.position.x}%`, top: `${port.position.y}%`, transform: "translate(-50%, -50%)",
                            width: isOutput ? "12px" : "14px", height: isOutput ? "12px" : "20px", 
                            backgroundColor: isOutput ? port.color : (isConnected ? connection.color : "rgba(255,0,0,0.3)"),
                            border: isOutput ? "2px solid #fff" : "2px dashed red", borderRadius: isOutput ? "50%" : "3px", cursor: isOutput ? "grab" : (isConnected ? "pointer" : "crosshair"), zIndex: 3, boxShadow: "0 2px 4px rgba(0,0,0,0.4)", display: "flex", justifyContent: "center", alignItems: "center"
                          }}>
                          {!isOutput && isConnected && <span style={{color: "white", fontSize: "14px", fontWeight: "bold", textShadow: "0px 1px 2px #000"}}>✔</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {activeManual && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%", 
          backgroundColor: "rgba(0,0,0,0.8)", zIndex: 99999, 
          display: "flex", justifyContent: "center", alignItems: "center"
        }}>
          <div style={{
            backgroundColor: "#fff", width: "800px", maxWidth: "90%", maxHeight: "90vh", 
            borderRadius: "12px", overflow: "hidden", display: "flex", flexDirection: "column",
            boxShadow: "0 20px 50px rgba(0,0,0,0.5)"
          }}>
            <div style={{ backgroundColor: "#333", color: "#fff", padding: "15px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0, fontSize: "20px" }}>📖 HDSD: {activeManual.name}</h2>
              <button onClick={() => setActiveManual(null)} style={{ background: "none", border: "none", color: "#fff", fontSize: "24px", cursor: "pointer" }}>✖</button>
            </div>

            <div style={{ padding: "30px", overflowY: "auto", flex: 1 }}>
              {manualData ? (
                <div>
                  <h3 style={{ borderBottom: "2px solid #007bff", paddingBottom: "5px", color: "#007bff" }}>{manualData.title}</h3>
                  
                  <div style={{ display: "flex", gap: "20px", marginTop: "20px" }}>
                    <div style={{ flex: 1 }}>
                      {manualData.buttons && (
                        <>
                          <h4>Nút Bấm & Chức Năng</h4>
                          <ul style={{ lineHeight: "1.8" }}>
                            {manualData.buttons.map((btn, idx) => (
                              <li key={idx}><strong>{btn.split(':')[0]}:</strong>{btn.split(':')[1]}</li>
                            ))}
                          </ul>
                        </>
                      )}

                      {manualData.pairing && (
                        <>
                          <h4 style={{ marginTop: "15px" }}>Cách Ghép Đôi (Pairing)</h4>
                          <ol style={{ lineHeight: "1.8" }}>
                            {manualData.pairing.map((step, idx) => <li key={idx}>{step}</li>)}
                          </ol>
                        </>
                      )}
                    </div>
                    
                    <div style={{ width: "250px", textAlign: "center" }}>
                      <img src={activeManual.image_url} alt="Device Image" style={{ width: "100%", border: "1px solid #ccc", borderRadius: "8px", padding: "10px" }} />
                      <p style={{ fontSize: "12px", color: "#666", marginTop: "10px" }}>{manualData.protocol}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "50px 20px" }}>
                  <span style={{ fontSize: "50px" }}>🚧</span>
                  <h3>Chưa có HDSD</h3>
                  <p style={{ color: "#666" }}>Tài liệu hướng dẫn cho <strong>{activeManual.name}</strong> chưa được cập nhật vào kho dữ liệu.</p>
                </div>
              )}
            </div>
            
            <div style={{ backgroundColor: "#f4f4f4", padding: "15px", textAlign: "right", borderTop: "1px solid #ddd" }}>
              <button onClick={() => setActiveManual(null)} style={{ padding: "8px 20px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "5px", cursor: "pointer", fontWeight: "bold" }}>Đóng (Close)</button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes blinkUp { 0% { transform: translateY(0); opacity: 0.3; } 100% { transform: translateY(-15px); opacity: 1; } }
          @keyframes blinkDown { 0% { transform: translateY(0); opacity: 0.3; } 100% { transform: translateY(15px); opacity: 1; } }
          @keyframes blinkJog { 
            0% { transform: translateY(0); opacity: 0.3; } 
            25% { transform: translateY(-10px); opacity: 1; } 
            50% { transform: translateY(0); opacity: 0.3; } 
            75% { transform: translateY(10px); opacity: 1; } 
            100% { transform: translateY(0); opacity: 0.3; } 
          }
          @keyframes curtainOpen { 0% { letter-spacing: -15px; opacity: 0.3; } 100% { letter-spacing: 30px; opacity: 1; } }
          @keyframes curtainClose { 0% { letter-spacing: 30px; opacity: 0.3; } 100% { letter-spacing: -15px; opacity: 1; } }
          @keyframes blinkJogHorizontal { 
            0% { transform: translateX(0); opacity: 0.3; } 
            25% { transform: translateX(-10px); opacity: 1; } 
            50% { transform: translateX(0); opacity: 0.3; } 
            75% { transform: translateX(10px); opacity: 1; } 
            100% { transform: translateX(0); opacity: 0.3; } 
          }
          @keyframes venetianTilt { 0% { transform: skewX(0deg); opacity: 0.5; } 100% { transform: skewX(-30deg); opacity: 1; } }
        `}
      </style>
    </div>
  );
}